/**
 * Worker shift-report main screen — §4.1 (redesign по мокапам 16.05),
 * §20/14.1 (компактная шапка: 3 полосы → 2).
 *
 * Плоский список записей вместо ленты карточек-accordion. Шапка из 2 полос:
 * (1) бренд-бар FieldShell с date-chip между GUDWIN и «ВЫХОД»,
 * (2) ФИО + должность top-aligned (sticky). Backfill-плашка остаётся
 * в sticky-блоке под ФИО. Полоса `ShiftStatStrip` удалена в §20/14.1 —
 * норма смены переехала в нижний StickyBar строкой «По табелю N ч».
 *
 * Создание/редактирование записи открывает overlay NewEntryFlow (§4.2).
 * Удаление вынесено внутрь wizard'a — на EntryRow нет ✕, только ✎.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { fieldIndexRoute } from "@/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, Loader2, Plus } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ru as ruLocale, enUS } from "date-fns/locale";

import { FieldShell } from "@/layout/FieldShell";
import { useMe } from "@/hooks/useMe";
import type { ApiError } from "@/api/client";
import { deleteFieldEntry, getFieldTimesheet } from "@/api/field-timesheet";
import type { FieldTimesheetResponse, TimesheetEntry } from "@/api/field-timesheet";
import { Button } from "@/components/ui/button";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { EntryRow } from "@/components/field/EntryRow";
import { NewEntryFlow } from "@/components/field/NewEntryFlow";
import { formatHoursHalf, sumHoursHalf } from "@/lib/timesheet";
import { showApiErrorToast } from "@/lib/api-error-toast";
import {
  buildProjectMap,
  buildWorkTypeCatalogs,
  fmtH,
  groupEntriesByProject,
  type ProjectGroup,
} from "@/lib/field-summary";
import { useFieldOfflineDrain } from "@/hooks/useFieldOfflineDrain";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Check } from "lucide-react";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";

const LOCALES = { ru: ruLocale, en: enUS } as const;

function resolveLocale(locale?: string): (typeof LOCALES)["ru"] {
  const key = (locale ?? "ru").slice(0, 2) as keyof typeof LOCALES;
  return LOCALES[key] ?? LOCALES.ru;
}

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** «14.05 СР» — compact-дата для триггера date-picker. EEEEEE = двухбуквенные
 *  дни недели (пн/вт/ср), которые внешний `uppercase`-стиль превращает в
 *  «ПН/ВТ/СР». §20/7.1 — фидбек Антона: трёхбуквенные «срд» обрезались. */
function formatChip(iso: string, locale: string): string {
  return format(parseISO(`${iso}T00:00:00`), "dd.MM EEEEEE", {
    locale: resolveLocale(locale),
  });
}

function relativeLabel(
  iso: string,
  today: string,
  locale: string,
  t: (k: string) => string,
): string {
  const a = parseISO(`${iso}T00:00:00`);
  const b = parseISO(`${today}T00:00:00`);
  const diff = Math.round((b.getTime() - a.getTime()) / 86_400_000);
  if (diff === 0) return t("field.main.date.today");
  if (diff === 1) return t("field.main.date.yesterday");
  if (diff === 2) return t("field.main.date.dayBefore");
  return format(a, "EEEE", { locale: resolveLocale(locale) });
}

// ---------------------------------------------------------------------------
// Overlay state (§4.2 stub)
// ---------------------------------------------------------------------------

type OverlayState = { mode: "create" } | { mode: "edit"; entry: TimesheetEntry };

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FieldTimesheetPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { user } = useMe();
  const navigate = useNavigate();

  const today = todayIso();
  // `?date=YYYY-MM-DD` — стартовая дата при возврате с FieldSignPage после
  // подписи backfill-дня; иначе сегодня.
  const search = fieldIndexRoute.useSearch();
  const [activeDate, setActiveDate] = useState<string>(search.date ?? today);
  useFieldOfflineDrain(user?.id ?? null);
  const [dateSheetOpen, setDateSheetOpen] = useState(false);
  const [overlay, setOverlay] = useState<OverlayState | null>(null);
  // §20/18.x — отдельный модальный confirm-delete прямо со строки списка
  // (без захода в wizard). Хранит выбранную запись; null → модал закрыт.
  const [deletingEntry, setDeletingEntry] = useState<TimesheetEntry | null>(null);
  const queryClient = useQueryClient();
  const deleteMut = useMutation({
    mutationFn: (entryId: number) => deleteFieldEntry(entryId, activeDate),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["timesheet", activeDate] });
      setDeletingEntry(null);
    },
    onError: (err: unknown) => {
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    },
  });
  const [forceEditByDate, setForceEditByDate] = useState<Record<string, true>>({});
  // §20/1.1 — id только что сохранённой entry для flash-affordance в EntryRow.
  // Заменяет sonner-toast «Сохранено», который перекрывал primary CTA.
  const [flashEntryId, setFlashEntryId] = useState<number | null>(null);
  useEffect(() => {
    if (flashEntryId === null) return;
    const tid = window.setTimeout(() => setFlashEntryId(null), 1000);
    return () => window.clearTimeout(tid);
  }, [flashEntryId]);

  // Участие в учёте времени (task 110 §2.1). Снятый флаг закрывает self-ввод на
  // бэкенде (§1.2, 403 на /field/timesheet/*), поэтому не дёргаем запрос и
  // показываем мягкое состояние вместо формы — вести сотрудника больше некуда
  // (решение владельца: не хард-редирект, а пояснение).
  const trackingOff = user != null && user.tracks_time === false;

  const query = useQuery<FieldTimesheetResponse, ApiError>({
    queryKey: ["timesheet", activeDate] as const,
    queryFn: () => getFieldTimesheet(activeDate),
    retry: false,
    staleTime: 0,
    enabled: !trackingOff,
  });

  // Резолвим имена work_types для EntryRow. useMemo обязан стоять до
  // early-returns ниже — rules-of-hooks. Source = data.my_work_types или
  // пустой массив, если данных ещё нет.
  const myWorkTypes = query.data?.my_work_types;
  const workTypesById = useMemo(() => {
    const m = new Map<number, string>();
    if (!myWorkTypes) return m;
    for (const w of myWorkTypes) {
      const id = w.id;
      if (id !== null && id !== undefined) m.set(id, w.name);
    }
    return m;
  }, [myWorkTypes]);

  // Учёт времени отключён у сотрудника — мягкое состояние вместо формы ввода
  // (task 110 §2.1). Стоит до query-веток: запрос отключён (`enabled`).
  if (trackingOff) {
    return (
      <FieldShell context={null}>
        <div
          className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center"
          data-testid="field-tracking-off"
        >
          <div
            className="font-condensed text-[18px] font-semibold uppercase leading-tight tracking-[0.08em]"
            style={{ color: "var(--fg-1)" }}
          >
            {t("field.main.trackingOff.title")}
          </div>
          <div className="max-w-[280px] text-[13px] leading-snug" style={{ color: "var(--fg-3)" }}>
            {t("field.main.trackingOff.subtitle")}
          </div>
        </div>
      </FieldShell>
    );
  }

  // 410 backfill_window_exceeded — пользователь руками влез в URL глубже окна.
  if (
    query.error?.status === 410 &&
    typeof query.error.body === "object" &&
    query.error.body !== null &&
    "error" in query.error.body &&
    (query.error.body as { error?: unknown }).error === "backfill_window_exceeded"
  ) {
    return (
      <FieldShell context={null}>
        <div className="flex h-full items-center justify-center p-6 text-center">
          <p
            className="font-condensed text-sm uppercase tracking-[0.15em]"
            style={{ color: "var(--fg-2)" }}
          >
            {t("field.main.error.backfillWindow")}
          </p>
        </div>
      </FieldShell>
    );
  }

  if (query.isError) {
    return (
      <FieldShell context={null}>
        <div className="flex h-full items-center justify-center p-6 text-center">
          <ApiErrorNotice error={query.error} fallbackMessage={t("common.apiError.fallback")} />
        </div>
      </FieldShell>
    );
  }

  if (query.isPending || !query.data) {
    return (
      <FieldShell context={null}>
        <div
          className="flex h-full items-center justify-center"
          data-testid="field-timesheet-loading"
        >
          <Loader2
            className="h-6 w-6 animate-spin"
            style={{ color: "var(--fg-3)" }}
            aria-hidden="true"
          />
        </div>
      </FieldShell>
    );
  }

  const data = query.data;
  const entries = data.entries;
  const totalHalf = sumHoursHalf(entries);
  const hasEntries = entries.length > 0;
  const status = data.timesheet.status;
  const isApproved = status === "approved";
  const isSigned = status === "signed";
  const forceEdit = forceEditByDate[activeDate] === true;
  const showReadOnly = isApproved || (isSigned && !forceEdit);

  const recentProjects = data.my_recent_projects;
  const projectsById = buildProjectMap(recentProjects);

  function handleDateChange(next: string): void {
    setActiveDate(next);
    setOverlay(null);
    setDateSheetOpen(false);
    // Сохраняем активный день в URL, чтобы рефреш и возврат с
    // FieldSignPage не сбрасывали backfill на сегодня.
    void navigate({
      to: "/field",
      search: next === today ? {} : { date: next },
      replace: true,
    });
  }

  function handleEditSigned(): void {
    setForceEditByDate((prev) => ({ ...prev, [activeDate]: true }));
  }

  const dateChip = (
    <DateChip
      activeDate={activeDate}
      locale={i18n.language}
      onPickDate={() => setDateSheetOpen(true)}
      aria={t("field.main.date.changeAria")}
    />
  );

  if (showReadOnly) {
    const { workTypeNameById } = buildWorkTypeCatalogs(data.my_work_types);
    const groups = groupEntriesByProject(entries, projectsById, workTypeNameById);
    return (
      <FieldShell context={null} topBarSlot={dateChip}>
        <PageScroll>
          <StickyHeader
            name={data.me.full_name || user?.display_name || ""}
            workRole={data.me.work_role ?? null}
            backfillDaysAgo={data.is_backfill ? data.backfill_days_ago : null}
            backfillLabel={t("field.main.backfill.label")}
            backfillDaysWord={t("field.main.backfill.days", {
              count: data.backfill_days_ago,
            })}
          />
          <ReadOnlyReport
            status={isApproved ? "approved" : "signed"}
            groups={groups}
            totalHalf={totalHalf}
            declaredHalf={data.timesheet.declared_total_hours_half}
            onEdit={isApproved ? null : handleEditSigned}
            labels={{
              signed: t("field.main.readonly.signed"),
              approved: t("field.main.readonly.approved"),
              summaryTitle: t("field.sign.summary.title"),
              total: t("field.sign.summary.total"),
              declaredLabel: t("field.sign.declared.title"),
              empty: t("field.sign.summary.empty"),
              edit: t("field.main.readonly.edit"),
              editHint: t("field.main.readonly.editHint"),
            }}
          />
        </PageScroll>
        <DateSheet
          open={dateSheetOpen}
          onOpenChange={setDateSheetOpen}
          allowedDates={data.allowed_dates}
          activeDate={activeDate}
          today={today}
          locale={i18n.language}
          title={t("field.main.dateSheet.title")}
          onPick={handleDateChange}
          t={t}
        />
      </FieldShell>
    );
  }

  return (
    <FieldShell context={null} topBarSlot={dateChip}>
      <PageScroll>
        <StickyHeader
          name={data.me.full_name || user?.display_name || ""}
          workRole={data.me.work_role ?? null}
          backfillDaysAgo={data.is_backfill ? data.backfill_days_ago : null}
          backfillLabel={t("field.main.backfill.label")}
          backfillDaysWord={t("field.main.backfill.days", {
            count: data.backfill_days_ago,
          })}
        />

        <div
          className="flex flex-col pt-4"
          data-testid="field-timesheet-feed"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 104px)",
          }}
        >
          {hasEntries ? (
            <ol
              className="flex flex-col"
              data-testid="entry-list"
              style={{ listStyle: "none", padding: 0, margin: 0 }}
            >
              {entries.map((e, idx) => (
                <EntryRow
                  key={e.id ?? `${e.timesheet_id}-${e.created_at}-${idx}`}
                  entry={e}
                  index={idx + 1}
                  projectsById={projectsById}
                  workTypesById={workTypesById}
                  onEdit={(entry) => setOverlay({ mode: "edit", entry })}
                  onDelete={(entry) => setDeletingEntry(entry)}
                  flash={e.id !== null && e.id !== undefined && e.id === flashEntryId}
                />
              ))}
            </ol>
          ) : (
            <EmptyState
              title={t("field.main.emptyState.title")}
              subtitle={t("field.main.emptyState.subtitle")}
              hint={t("field.main.emptyState.hint")}
            />
          )}

          <AddEntryCta
            label={hasEntries ? t("field.main.addMore") : t("field.main.addEntry")}
            hint={hasEntries ? t("field.main.addMoreHint") : null}
            onClick={() => setOverlay({ mode: "create" })}
          />
        </div>
      </PageScroll>

      <StickyBar
        totalHalf={totalHalf}
        hasEntries={hasEntries}
        labelTotal={t("field.main.stat.totalWorks")}
        labelNorm={t("field.main.stat.norm", {
          hours: formatHoursHalf(data.default_declared_hours_half),
        })}
        labelSign={t("field.main.sign")}
        disabledHint={t("field.main.signDisabledHint")}
        onSign={() =>
          navigate({
            to: "/field/sign",
            search: activeDate === today ? undefined : { date: activeDate },
          })
        }
      />

      <DateSheet
        open={dateSheetOpen}
        onOpenChange={setDateSheetOpen}
        allowedDates={data.allowed_dates}
        activeDate={activeDate}
        today={today}
        locale={i18n.language}
        title={t("field.main.dateSheet.title")}
        onPick={handleDateChange}
        t={t}
      />

      {overlay && (
        <NewEntryFlow
          mode={overlay.mode}
          initialEntry={overlay.mode === "edit" ? overlay.entry : undefined}
          recentProjects={recentProjects}
          myWorkTypes={data.my_work_types}
          activeDate={activeDate}
          onClose={() => setOverlay(null)}
          onSaved={(entry) => {
            if (entry.id !== null && entry.id !== undefined) {
              setFlashEntryId(entry.id);
            }
            setOverlay(null);
          }}
          onDeleted={() => setOverlay(null)}
        />
      )}

      <AlertDialogPrimitive.Root
        open={deletingEntry !== null}
        onOpenChange={(v) => {
          if (!v && !deleteMut.isPending) setDeletingEntry(null);
        }}
      >
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="fixed inset-0 z-50 bg-black/30"
            data-testid="entry-delete-overlay"
          />
          <AlertDialogPrimitive.Content
            className="fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 p-4 outline-none"
            style={{
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-strong, var(--rule-line))",
              borderRadius: "var(--radius-card)",
              boxShadow: "var(--shadow-plate)",
            }}
            data-testid="entry-delete-confirm"
          >
            <AlertDialogPrimitive.Title
              className="font-condensed text-[15px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: "var(--fg-1)" }}
            >
              {t("field.main.entries.deleteTitle")}
            </AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Description
              className="text-[13px] leading-snug"
              style={{ color: "var(--fg-2)" }}
            >
              {t("field.main.entries.deleteBody")}
            </AlertDialogPrimitive.Description>
            <div className="flex items-center justify-end gap-2">
              <AlertDialogPrimitive.Cancel asChild>
                <Button variant="ghost" size="sm" data-testid="entry-delete-cancel">
                  {t("field.main.entries.deleteCancel")}
                </Button>
              </AlertDialogPrimitive.Cancel>
              <Button
                variant="default"
                size="sm"
                disabled={deleteMut.isPending || deletingEntry?.id == null}
                onClick={() => {
                  if (deletingEntry?.id != null) {
                    deleteMut.mutate(deletingEntry.id);
                  }
                }}
                data-testid="entry-delete-ok"
                style={{ background: "#9a3412", color: "#fff" }}
              >
                {t("field.main.entries.deleteOk")}
              </Button>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialogPrimitive.Root>
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------
// PageScroll — flex-колонка для тела страницы. FieldShell.main без padding
// (вложенные блоки сами держат свои px-4/py-N), чтобы sticky-header
// корректно прижимался к верхнему краю scroll-контейнера.
// ---------------------------------------------------------------------------

function PageScroll({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="flex flex-col">{children}</div>;
}

// ---------------------------------------------------------------------------
// NameStack — ФИО: фамилия (крупно) + имя-отчество (мельче, приглушённо) в одной
// flex-строке. Если влезает — одна строка (компактно), не влезает — имя-отчество
// переносится на следующую строку (`flex-wrap`), как было раньше. Многоточием не
// режем — обрезать имя человека на его же экране выглядит как баг. Длинные слитные
// фамилии ломаются `break-words` + `overflow-wrap: anywhere`.
// ---------------------------------------------------------------------------

function NameStack({ name }: { name: string }): React.ReactElement {
  const trimmed = (name ?? "").trim();
  if (!trimmed) {
    return (
      <div
        className="font-condensed text-[20px] font-semibold uppercase leading-tight tracking-[0.04em]"
        style={{ color: "var(--fg-1)" }}
      >
        —
      </div>
    );
  }
  const parts = trimmed.split(/\s+/);
  const lastName = parts[0];
  const rest = parts.slice(1).join(" ");
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5">
      <span
        className="font-condensed text-[20px] font-semibold uppercase leading-tight tracking-[0.04em] break-words [overflow-wrap:anywhere]"
        style={{ color: "var(--fg-1)" }}
        data-testid="field-profile-name-last"
      >
        {lastName}
      </span>
      {rest && (
        <span
          className="font-condensed text-[16px] uppercase leading-tight tracking-[0.04em] break-words [overflow-wrap:anywhere]"
          style={{ color: "var(--fg-3)" }}
          data-testid="field-profile-name-first"
        >
          {rest}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DateChip — триггер DateSheet, переехал из ФИО-полосы в бренд-бар FieldShell
// (§20/14.1). Тот же визуал: outline-кнопка с компактной датой «14.05 СР»
// и chevron.
// ---------------------------------------------------------------------------

interface DateChipProps {
  activeDate: string;
  locale: string;
  onPickDate: () => void;
  aria: string;
}

function DateChip({ activeDate, locale, onPickDate, aria }: DateChipProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onPickDate}
      className="inline-flex h-8 shrink-0 items-center gap-1.5 px-2.5 font-condensed text-[12px] uppercase tracking-[0.08em] transition-colors"
      style={{
        color: "var(--fg-1)",
        borderRadius: "var(--radius-button)",
        border: "1px solid var(--rule-line)",
        background: "var(--bg-panel-0)",
      }}
      data-testid="field-date-trigger"
      aria-label={aria}
    >
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatChip(activeDate, locale)}</span>
      <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// StickyHeader — sticky-блок под бренд-баром: ФИО сверху / должность снизу
// вертикальным стеком на полную ширину + бордовая backfill-плашка. Стек (а не
// две колонки) — чтобы длинные должности заняли 2 строки вместо 5. §20/14.1:
// date-chip переехал в бренд-бар FieldShell (`topBarSlot`), полоса
// `ShiftStatStrip` удалена, норма смены теперь в нижнем `StickyBar` строкой
// «По табелю N ч».
// ---------------------------------------------------------------------------

interface StickyHeaderProps {
  name: string;
  workRole: string | null;
  backfillDaysAgo: number | null;
  backfillLabel: string;
  backfillDaysWord: string;
}

export function StickyHeader({
  name,
  workRole,
  backfillDaysAgo,
  backfillLabel,
  backfillDaysWord,
}: StickyHeaderProps): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-20"
      style={{ background: "var(--bg-panel-0)" }}
      data-testid="field-sticky-header"
    >
      <div className="px-4 pt-3 pb-3" style={{ borderBottom: "1px solid var(--rule-line)" }}>
        <div className="min-w-0" data-testid="field-profile-name">
          <NameStack name={name} />
        </div>
        {workRole && (
          <div
            className="mt-1.5 font-condensed text-[12px] uppercase leading-tight tracking-[0.14em] break-words [overflow-wrap:anywhere]"
            style={{ color: "var(--fg-3)" }}
            data-testid="field-profile-role"
          >
            {workRole}
          </div>
        )}
      </div>

      {backfillDaysAgo !== null && (
        <div
          className="flex items-center gap-2 px-4 py-2 text-white"
          style={{ background: "#7a1f1f" }}
          role="status"
          data-testid="field-backfill-plate"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <div className="font-condensed text-[12px] uppercase leading-tight tracking-[0.1em]">
            {backfillLabel} <span style={{ opacity: 0.8 }}>·</span>{" "}
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{backfillDaysAgo}</span>{" "}
            {backfillDaysWord}
          </div>
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  title: string;
  subtitle: string;
  hint: string;
}

function EmptyState({ title, subtitle, hint }: EmptyStateProps): React.ReactElement {
  return (
    <div
      className="flex flex-col items-center justify-center px-6 pt-16 pb-6 text-center"
      data-testid="field-empty-state"
    >
      <div
        className="font-condensed text-[18px] font-semibold uppercase leading-tight tracking-[0.08em]"
        style={{ color: "var(--fg-1)" }}
      >
        {title}
      </div>
      <div className="mt-3 max-w-[280px] text-[13px] leading-snug" style={{ color: "var(--fg-3)" }}>
        {subtitle}
      </div>
      <div
        className="mt-6 font-condensed text-[15px] font-semibold uppercase tracking-[0.18em]"
        style={{ color: "var(--fg-2)" }}
        data-testid="field-empty-hint"
      >
        {hint}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddEntryCta — НЕ sticky, идёт после empty-state / списка.
// ---------------------------------------------------------------------------

interface AddEntryCtaProps {
  label: string;
  hint: string | null;
  onClick: () => void;
}

function AddEntryCta({ label, hint, onClick }: AddEntryCtaProps): React.ReactElement {
  return (
    <div className="px-4 pt-4 pb-2">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-center gap-2 font-condensed text-[14px] font-semibold uppercase tracking-[0.14em] transition-colors"
        style={{
          minHeight: 56,
          color: "var(--fg-1)",
          background: "var(--bg-panel-0)",
          border: "1px dashed var(--rule-strong, var(--rule-line))",
          borderRadius: "var(--radius-button)",
        }}
        data-testid="field-add-entry"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {label}
      </button>
      {hint && (
        <div
          className="mt-2 text-center font-condensed text-[11px] uppercase tracking-[0.18em]"
          style={{ color: "var(--fg-3)" }}
          data-testid="field-add-hint"
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StickyBar — bottom: Σ + Подписать
// ---------------------------------------------------------------------------

interface StickyBarProps {
  totalHalf: number;
  hasEntries: boolean;
  labelTotal: string;
  /** Готовая строка «По табелю 8.0 ч» (i18n + formatHoursHalf снаружи). */
  labelNorm: string;
  labelSign: string;
  disabledHint: string;
  onSign: () => void;
}

export function StickyBar({
  totalHalf,
  hasEntries,
  labelTotal,
  labelNorm,
  labelSign,
  disabledHint,
  onSign,
}: StickyBarProps): React.ReactElement {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 px-4 pt-3"
      style={{
        background: "var(--bg-panel-0)",
        borderTop: "1px solid var(--rule-line)",
        boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.08)",
        paddingBottom: "max(8px, env(safe-area-inset-bottom, 8px))",
      }}
      data-testid="field-sticky-bar"
    >
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-2">
          <span
            className="font-condensed text-[11px] font-bold uppercase tracking-[0.22em]"
            style={{ color: "var(--fg-2)" }}
          >
            {labelTotal}
          </span>
          <span
            className="font-condensed text-[18px] font-semibold tabular-nums"
            style={{ color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}
            data-testid="field-total"
          >
            {formatHoursHalf(totalHalf)}
          </span>
        </div>
        <span
          className="font-condensed text-[11px] uppercase leading-tight tracking-[0.14em]"
          style={{ color: "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}
          data-testid="field-total-norm"
        >
          {labelNorm}
        </span>
      </div>
      {(() => {
        const signButton = (
          <button
            type="button"
            onClick={onSign}
            disabled={!hasEntries}
            className="inline-flex h-12 items-center gap-2 px-5 font-condensed text-[13px] font-semibold uppercase tracking-[0.1em] transition-colors disabled:cursor-not-allowed"
            style={{
              color: hasEntries ? "var(--fg-inverse)" : "var(--fg-4)",
              background: hasEntries ? "var(--soot-900, #16181b)" : "var(--bg-panel-2)",
              borderRadius: "var(--radius-button)",
            }}
            data-testid="field-sign-button"
          >
            {labelSign} →
          </button>
        );
        if (hasEntries) return signButton;
        // disabled-кнопка не получает pointer-событий — обёртка <span tabIndex>
        // даёт <Tooltip> сработать «почему недоступно» на disabled (§2.7, EDGE 1b).
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex">
                {signButton}
              </span>
            </TooltipTrigger>
            <TooltipContent>{disabledHint}</TooltipContent>
          </Tooltip>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DateSheet — bottom-sheet «За какой день?»
// ---------------------------------------------------------------------------

interface DateSheetProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  allowedDates: string[];
  activeDate: string;
  today: string;
  locale: string;
  title: string;
  onPick: (date: string) => void;
  t: (k: string) => string;
}

function DateSheet({
  open,
  onOpenChange,
  allowedDates,
  activeDate,
  today,
  locale,
  title,
  onPick,
  t,
}: DateSheetProps): React.ReactElement {
  const items = [...allowedDates].reverse();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-[2px] p-0" data-testid="field-date-sheet">
        <SheetHeader>
          <SheetTitle className="font-condensed text-[14px] uppercase tracking-[0.14em]">
            {title}
          </SheetTitle>
          <SheetDescription className="sr-only">{title}</SheetDescription>
        </SheetHeader>
        <ul className="flex flex-col" style={{ listStyle: "none", padding: 0 }}>
          {items.map((iso) => {
            const isActive = iso === activeDate;
            return (
              <li key={iso}>
                <button
                  type="button"
                  onClick={() => onPick(iso)}
                  className="flex w-full items-baseline gap-3 px-4 py-3 text-left transition-colors"
                  style={{
                    background: isActive ? "var(--bg-panel-2)" : "transparent",
                    borderTop: "1px solid var(--rule-line)",
                  }}
                  data-testid="field-date-option"
                  data-date={iso}
                  aria-current={isActive ? "date" : undefined}
                >
                  <span
                    className="font-condensed text-[15px] uppercase tabular-nums"
                    style={{
                      color: "var(--fg-1)",
                      fontVariantNumeric: "tabular-nums",
                      minWidth: 88,
                    }}
                  >
                    {formatChip(iso, locale)}
                  </span>
                  <span
                    className="font-condensed text-[11px] uppercase tracking-[0.16em]"
                    style={{ color: "var(--fg-3)" }}
                  >
                    {relativeLabel(iso, today, locale, t)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <div style={{ height: "max(8px, env(safe-area-inset-bottom, 8px))" }} />
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// ReadOnlyReport — signed/approved сводка (сохраняется из §3.6 без изменений
// логики, только адаптирована к новой шапке).
// ---------------------------------------------------------------------------

interface ReadOnlyReportProps {
  status: "signed" | "approved";
  groups: readonly ProjectGroup[];
  totalHalf: number;
  declaredHalf: number;
  onEdit: (() => void) | null;
  labels: {
    signed: string;
    approved: string;
    summaryTitle: string;
    total: string;
    declaredLabel: string;
    empty: string;
    edit: string;
    editHint: string;
  };
}

function ReadOnlyReport({
  status,
  groups,
  totalHalf,
  declaredHalf,
  onEdit,
  labels,
}: ReadOnlyReportProps): React.ReactElement {
  const isApproved = status === "approved";
  return (
    <>
      <div
        className="mx-4 mt-6 flex items-start gap-3 px-3 py-3"
        data-testid="field-readonly-banner"
        style={{
          borderRadius: "var(--radius-card)",
          background: isApproved ? "var(--bg-panel-2)" : "var(--bg-panel-1)",
          border: "1px solid var(--rule-line)",
          color: "var(--fg-2)",
        }}
      >
        <Check
          aria-hidden
          className="mt-0.5 h-5 w-5 shrink-0"
          style={{
            color: isApproved ? "var(--soot-900, #16181b)" : "var(--fg-1)",
          }}
        />
        <div className="min-w-0 flex-1">
          <div
            className="font-condensed text-[13px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--fg-1)" }}
          >
            {isApproved ? labels.approved : labels.signed}
          </div>
          {!isApproved && (
            <div className="mt-0.5 text-[12px] leading-snug" style={{ color: "var(--fg-3)" }}>
              {labels.editHint}
            </div>
          )}
        </div>
      </div>

      <div
        className="flex flex-col"
        data-testid="field-readonly-body"
        style={{
          paddingBottom: onEdit
            ? "calc(env(safe-area-inset-bottom, 0px) + 96px)"
            : "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
      >
        <section className="px-4 pt-5 pb-6" data-testid="field-readonly-summary">
          <h2
            className="font-condensed text-[11px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: "var(--fg-2)", fontFamily: "var(--font-display)" }}
          >
            {labels.summaryTitle}
          </h2>

          {groups.length === 0 ? (
            <div
              className="mt-4 px-1 py-3 font-condensed text-[12px] uppercase leading-snug tracking-[0.1em]"
              style={{ color: "var(--fg-3)" }}
              data-testid="field-readonly-empty"
            >
              {labels.empty}
            </div>
          ) : (
            <ul
              className="mt-3 flex flex-col"
              style={{ listStyle: "none", padding: 0 }}
              data-testid="field-readonly-list"
            >
              {groups.map((g) => (
                <ReadOnlyProjectGroup key={g.projectKey} group={g} />
              ))}
            </ul>
          )}

          <div
            className="mt-4 flex items-baseline justify-between border-t pt-3"
            style={{ borderColor: "var(--rule-line)" }}
          >
            <span
              className="font-condensed text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "var(--fg-3)" }}
            >
              {labels.total}
            </span>
            <span
              className="font-display text-[26px] font-semibold leading-none tabular-nums"
              style={{
                color: "var(--fg-1)",
                fontFamily: "var(--font-display)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtH(totalHalf)}
            </span>
          </div>

          <div className="mt-3 flex items-baseline justify-between">
            <span
              className="font-condensed text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "var(--fg-3)" }}
            >
              {labels.declaredLabel}
            </span>
            <span
              className="font-display text-[18px] font-semibold leading-none tabular-nums"
              style={{
                color: "var(--fg-2)",
                fontFamily: "var(--font-display)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtH(declaredHalf)}
            </span>
          </div>
        </section>
      </div>

      {onEdit && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 px-4 pt-3"
          style={{
            background: "var(--bg-panel-0)",
            borderTop: "1px solid var(--rule-line)",
            boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.08)",
            paddingBottom: "max(8px, env(safe-area-inset-bottom, 8px))",
          }}
          data-testid="field-readonly-sticky"
        >
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-12 flex-1 items-center justify-center gap-2 px-5 font-condensed text-[14px] font-semibold uppercase tracking-[0.12em] transition-colors"
            style={{
              color: "var(--fg-1)",
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-strong)",
              borderRadius: "var(--radius-button)",
            }}
            data-testid="field-readonly-edit"
          >
            {labels.edit}
          </button>
        </div>
      )}
    </>
  );
}

function ReadOnlyProjectGroup({ group }: { group: ProjectGroup }): React.ReactElement {
  return (
    <li
      className="flex flex-col py-2"
      style={{ borderTop: "1px solid var(--rule-line)" }}
      data-testid="field-readonly-project-group"
      data-project={group.projectKey}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="truncate font-condensed text-[16px] font-semibold uppercase leading-tight tracking-[0.04em]"
            style={{ color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}
          >
            {group.code}
            {group.isRaw && (
              <AlertTriangle
                className="ml-1 inline-block h-3.5 w-3.5 align-[-1px]"
                style={{ color: "#b45309" }}
                aria-hidden
              />
            )}
            {group.name && (
              <>
                <span style={{ color: "var(--fg-3)" }}> · </span>
                <span
                  className="font-condensed text-[15px] font-normal uppercase tracking-[0.04em]"
                  style={{ color: "var(--fg-2)" }}
                >
                  {group.name}
                </span>
              </>
            )}
          </div>
        </div>
        <div
          className="shrink-0 font-display text-[18px] font-semibold tabular-nums"
          style={{
            color: "var(--fg-1)",
            fontFamily: "var(--font-display)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtH(group.totalHalf)}
        </div>
      </div>
      <ul className="mt-1 ml-0 flex flex-col gap-0.5" style={{ listStyle: "none", padding: 0 }}>
        {group.rows.map((r) => (
          <li
            key={r.id}
            className="flex items-baseline gap-2 text-[12px] leading-snug"
            data-testid="field-readonly-row"
          >
            <span className="min-w-0 flex-1 truncate" style={{ color: "var(--fg-2)" }}>
              {r.workType ?? "—"}
            </span>
            <span
              className="shrink-0 tabular-nums"
              style={{ color: "var(--fg-2)", fontVariantNumeric: "tabular-nums" }}
            >
              {fmtH(r.hoursHalf)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}
